import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Tooltip, Typography } from "@mui/material";
import { BORDER_RADIUS, COLORS } from "app/theme";
import type { ReactNode } from "react";

const ALERT_COLOR = "#DC2626";

export interface StatTileProps {
  label: string;
  value: string;
  unit?: string;
  tooltip?: string;
  /** Coloured dot before the label (e.g. to key a tile to a chart series). */
  dotColor?: string;
  /** Leading icon, shown instead of a dot (e.g. for alert tiles). */
  icon?: ReactNode;
  /** Override the value colour (e.g. funnel stage colour). */
  valueColor?: string;
  /** Larger value text for hero tiles. */
  large?: boolean;
  /** When true and `active`, the tile turns red to flag a breach. */
  alert?: boolean;
  active?: boolean;
  maxWidth?: number;
}

/**
 * The single metric-tile style used across the programme dashboard. Keeps a
 * consistent container (white, soft purple border, medium radius) with a few
 * semantic accents — a coloured dot, a leading icon, or a red alert state —
 * rather than several bespoke card styles.
 */
export default function StatTile({
  label,
  value,
  unit,
  tooltip,
  dotColor,
  icon,
  valueColor,
  large = false,
  alert = false,
  active = false,
  maxWidth,
}: StatTileProps) {
  const isAlert = alert && active;
  const borderColor = isAlert ? `${ALERT_COLOR}40` : COLORS.bellflower[300];
  const background = isAlert ? `${ALERT_COLOR}0D` : "white";
  const numberColor = isAlert ? ALERT_COLOR : valueColor ?? "#18181b";

  return (
    <Box
      sx={{
        flex: "1 1 140px",
        minWidth: 0,
        maxWidth,
        backgroundColor: background,
        border: `1px solid ${borderColor}`,
        borderRadius: `${BORDER_RADIUS.medium}px`,
        px: 2,
        py: 1.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
        {icon ? (
          <Box
            sx={{
              display: "flex",
              color: isAlert ? ALERT_COLOR : "#a1a1aa",
            }}
          >
            {icon}
          </Box>
        ) : dotColor ? (
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: dotColor,
              flexShrink: 0,
            }}
          />
        ) : null}
        <Typography
          variant="caption"
          sx={{ color: "#71717a", lineHeight: 1.2 }}
        >
          {label}
        </Typography>
        {tooltip && (
          <Tooltip title={tooltip} arrow placement="top">
            <InfoOutlinedIcon
              sx={{ fontSize: 13, color: "#a1a1aa", cursor: "help" }}
            />
          </Tooltip>
        )}
      </Box>
      <Box
        sx={{ display: "flex", alignItems: "baseline", gap: 0.5, minWidth: 0 }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            color: numberColor,
            lineHeight: 1.1,
            fontSize: large ? "1.9rem" : "1.4rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {value}
        </Typography>
        {unit && (
          <Typography
            variant="caption"
            sx={{ color: "#a1a1aa", flexShrink: 0 }}
          >
            {unit}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
